'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import Image from 'next/image';
import { trackEvent } from '@/lib/analytics';
import dynamic from 'next/dynamic';
import {
  Check,
  ArrowRight,
  MessageSquare,
  Brain,
  ShieldCheck,
  Clock,
  Smartphone,
  Menu,
  X,
  Play,
  Star,
  Zap
} from 'lucide-react';
import { SupportChatWidget } from '@/components/SupportChatWidget';

const LightBackground = dynamic(() => import('@/components/LightBackground'), {
  ssr: false,
});

const WhatsAppMockup = dynamic(() => import('@/components/landing/WhatsAppMockup'), {
  ssr: false,
});

const DashboardPreview = dynamic(() => import('@/components/landing/DashboardPreview'), {
  ssr: false,
});

const ExitIntentPopup = dynamic(() => import('@/components/landing/ExitIntentPopup'), {
  ssr: false,
});

type LandingStats = {
  customers: number;
  messages: number;
};

const DEFAULT_STATS: LandingStats = {
  customers: 120,
  messages: 240000,
};

function formatNumber(value: number) {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
  return value.toString();
}

// Header Component
function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/80 backdrop-blur-lg border-b border-white/5' : 'bg-transparent'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-violet-500 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold font-jakarta text-white">Atende Turbo</span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#como-funciona" className="text-gray-300 hover:text-white transition-colors text-sm font-inter">
            Como Funciona
          </a>
          <a href="#precos" className="text-gray-300 hover:text-white transition-colors text-sm font-inter">
            Preços
          </a>
          <a href="#depoimentos" className="text-gray-300 hover:text-white transition-colors text-sm font-inter">
            Depoimentos
          </a>
          <Link
            href="/login"
            className="text-gray-300 hover:text-white transition-colors text-sm font-inter"
          >
            Entrar
          </Link>
          <Link
            href="/signup"
            onClick={() => trackEvent({ action: 'signup_start', category: 'conversion', label: 'header_cta' })}
            className="px-4 py-2 bg-white text-gray-900 rounded-lg font-semibold text-sm font-jakarta hover:bg-gray-100 transition-colors"
          >
            Começar Grátis
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-black/95 backdrop-blur-lg border-b border-white/5 px-4 py-6"
        >
          <nav className="flex flex-col gap-4">
            <a href="#como-funciona" className="text-gray-300 hover:text-white py-2 font-inter" onClick={() => setIsMobileMenuOpen(false)}>
              Como Funciona
            </a>
            <a href="#precos" className="text-gray-300 hover:text-white py-2 font-inter" onClick={() => setIsMobileMenuOpen(false)}>
              Preços
            </a>
            <a href="#depoimentos" className="text-gray-300 hover:text-white py-2 font-inter" onClick={() => setIsMobileMenuOpen(false)}>
              Depoimentos
            </a>
            <Link href="/login" className="text-gray-300 hover:text-white py-2 font-inter">
              Entrar
            </Link>
            <Link
              href="/signup"
              className="mt-2 px-4 py-3 bg-white text-gray-900 rounded-lg font-semibold text-center font-jakarta"
            >
              Começar Grátis
            </Link>
          </nav>
        </motion.div>
      )}
    </header>
  );
}

// Pricing Card Component
function PricingCard({
  name,
  price,
  description,
  features,
  highlighted = false,
  planId,
}: {
  name: string;
  price: string;
  description: string;
  features: string[];
  highlighted?: boolean;
  planId: string;
}) {
  return (
    <motion.div
      whileHover={{ scale: 1.02, y: -4 }}
      className={`rounded-2xl p-6 md:p-8 relative border ${
        highlighted
          ? 'bg-gradient-to-b from-blue-500/10 to-violet-500/10 border-blue-500/30'
          : 'bg-white/5 border-white/10'
      }`}
    >
      {highlighted && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gradient-to-r from-blue-500 to-violet-500 rounded-full text-white text-xs font-semibold">
          Mais Popular
        </div>
      )}
      <h3 className="text-2xl font-semibold font-jakarta text-white mb-2">{name}</h3>
      <div className="mb-4">
        <span className="text-4xl font-bold font-jakarta text-white">{price}</span>
        <span className="text-gray-400 text-sm font-inter">/mês</span>
      </div>
      <p className="text-gray-400 mb-6 text-sm font-inter">{description}</p>
      <ul className="space-y-3 mb-8">
        {features.map((feature, i) => (
          <li key={i} className="flex items-start gap-3 text-gray-300 text-sm font-inter">
            <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      <Link
        href={`/signup?plan=${planId}`}
        onClick={() => trackEvent({ action: 'signup_start', category: 'conversion', label: `pricing_${planId}` })}
        className={`block w-full py-4 rounded-xl font-semibold font-jakarta text-center transition-all duration-200 ${
          highlighted
            ? 'bg-white text-gray-900 hover:bg-gray-100'
            : 'bg-white/10 text-white hover:bg-white/20 border border-white/10'
        }`}
      >
        Começar Agora
      </Link>
    </motion.div>
  );
}

// Testimonial Card Component
function TestimonialCard({
  quote,
  author,
  role,
  rating = 5,
  avatar,
}: {
  quote: string;
  author: string;
  role: string;
  rating?: number;
  avatar?: string;
}) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="rounded-2xl p-6 bg-white/5 border border-white/10"
    >
      {/* Rating Stars */}
      <div className="flex gap-1 mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      <p className="text-gray-300 mb-6 font-inter leading-relaxed">&ldquo;{quote}&rdquo;</p>
      <div className="flex items-center gap-3">
        {avatar ? (
          <Image
            src={avatar}
            alt={author}
            width={40}
            height={40}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white font-bold">
            {author.charAt(0)}
          </div>
        )}
        <div>
          <div className="text-white font-semibold font-jakarta text-sm">{author}</div>
          <div className="text-gray-500 text-xs font-inter">{role}</div>
        </div>
      </div>
    </motion.div>
  );
}

// Main Page Component
export default function Home() {
  const [stats, setStats] = useState<LandingStats>(DEFAULT_STATS);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const res = await fetch('/api/public-stats', { cache: 'no-store' });
        if (!res.ok) return;
        const data = await res.json();
        setStats({
          customers: data.customers ?? DEFAULT_STATS.customers,
          messages: data.messages ?? DEFAULT_STATS.messages,
        });
      } catch {
        // Use defaults
      }
    };

    loadStats();
    trackEvent({ action: 'view_landing_page', category: 'landing', label: 'home' });
  }, []);

  return (
    <main className="min-h-screen bg-black relative overflow-hidden">
      <LightBackground />
      <Header />

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-4 pt-24 pb-16">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text Content */}
            <div className="text-center lg:text-left">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
            <span className="text-gray-300 text-sm font-inter">
              {formatNumber(stats.customers)}+ empresas já usam
            </span>
          </motion.div>

          {/* Headline - More Direct */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold font-jakarta text-white mb-6 leading-tight"
          >
            Venda mais,
            <br />
            <span className="bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
              respondendo menos
            </span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-400 font-inter mb-8 max-w-2xl mx-auto"
          >
            Sua IA atende clientes no WhatsApp 24h por dia.
            <span className="text-white"> Você só aprova e vende.</span>
          </motion.p>

          {/* Value Props */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 md:gap-6 mb-10 text-gray-400 text-sm"
          >
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Setup em 5 minutos</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>Sem precisar programar</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-400" />
              <span>7 dias grátis</span>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link
              href="/signup"
              onClick={() => trackEvent({ action: 'signup_start', category: 'conversion', label: 'hero_cta' })}
              className="group relative px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold font-jakarta text-lg hover:bg-gray-100 transition-all flex items-center gap-2"
            >
              Começar Teste Grátis
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="#como-funciona"
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors font-inter"
            >
              <Play className="w-5 h-5" />
              Ver como funciona
            </a>
          </motion.div>

          {/* Trust Badges */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-12 flex flex-wrap justify-center lg:justify-start gap-6 text-gray-500 text-xs font-inter"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              <span>Sem cartão de crédito</span>
            </div>
            <div className="flex items-center gap-2">
              <Smartphone className="w-4 h-4" />
              <span>WhatsApp pessoal ou business</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Cancele quando quiser</span>
            </div>
          </motion.div>
            </div>

            {/* Right: WhatsApp Mockup */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="hidden lg:block"
            >
              <WhatsAppMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <section className="py-8 px-4 border-y border-white/5 bg-white/[0.02] relative z-10">
        <div className="max-w-4xl mx-auto flex flex-wrap justify-center gap-8 md:gap-16">
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold font-jakarta text-white">{formatNumber(stats.customers)}+</div>
            <div className="text-gray-500 text-sm font-inter">Empresas</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold font-jakarta text-white">{formatNumber(stats.messages)}+</div>
            <div className="text-gray-500 text-sm font-inter">Mensagens</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold font-jakarta text-white">24/7</div>
            <div className="text-gray-500 text-sm font-inter">Disponível</div>
          </div>
          <div className="text-center">
            <div className="text-3xl md:text-4xl font-bold font-jakarta text-white">4.9</div>
            <div className="text-gray-500 text-sm font-inter flex items-center gap-1">
              <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" /> Avaliação
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - Simplified */}
      <section id="como-funciona" className="py-20 md:py-28 px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-jakarta text-white mb-4">
              Como funciona
            </h2>
            <p className="text-gray-400 font-inter text-lg max-w-xl mx-auto">
              Em 3 passos simples, sua IA começa a atender
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <Smartphone className="w-8 h-8 text-blue-400" />
              </div>
              <div className="text-blue-400 text-sm font-semibold mb-2 font-inter">Passo 1</div>
              <h3 className="text-xl font-semibold font-jakarta text-white mb-2">Conecte seu WhatsApp</h3>
              <p className="text-gray-400 font-inter text-sm">
                Escaneie um QR Code e pronto. Funciona com WhatsApp pessoal ou Business.
              </p>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-violet-500/10 border border-violet-500/20 flex items-center justify-center">
                <Brain className="w-8 h-8 text-violet-400" />
              </div>
              <div className="text-violet-400 text-sm font-semibold mb-2 font-inter">Passo 2</div>
              <h3 className="text-xl font-semibold font-jakarta text-white mb-2">IA aprende seu negócio</h3>
              <p className="text-gray-400 font-inter text-sm">
                Importe seu site, PDFs ou escreva sobre seu negócio. A IA aprende tudo.
              </p>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-green-400" />
              </div>
              <div className="text-green-400 text-sm font-semibold mb-2 font-inter">Passo 3</div>
              <h3 className="text-xl font-semibold font-jakarta text-white mb-2">Comece a vender mais</h3>
              <p className="text-gray-400 font-inter text-sm">
                A IA responde 24h. Você aprova, edita ou deixa no automático.
              </p>
            </motion.div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-16">
            <DashboardPreview />
          </div>
        </div>
      </section>

      {/* Features - Only 2 Key Features */}
      <section className="py-20 px-4 relative z-10 bg-white/[0.02] border-y border-white/5">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Feature 1: Control */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl p-8 bg-gradient-to-br from-blue-500/5 to-transparent border border-white/10"
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-6">
                <ShieldCheck className="w-6 h-6 text-blue-400" />
              </div>
              <h3 className="text-2xl font-bold font-jakarta text-white mb-4">
                Você sempre no controle
              </h3>
              <p className="text-gray-400 font-inter mb-6 leading-relaxed">
                Comece aprovando cada resposta. Quando confiar, ative o modo automático. Intervenha quando quiser.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-blue-400" />
                  Modo assistido ou autônomo
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-blue-400" />
                  Edite respostas antes de enviar
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-blue-400" />
                  Tome controle a qualquer momento
                </li>
              </ul>
            </motion.div>

            {/* Feature 2: Learning */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="rounded-2xl p-8 bg-gradient-to-br from-violet-500/5 to-transparent border border-white/10"
            >
              <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mb-6">
                <Brain className="w-6 h-6 text-violet-400" />
              </div>
              <h3 className="text-2xl font-bold font-jakarta text-white mb-4">
                IA que aprende seu jeito
              </h3>
              <p className="text-gray-400 font-inter mb-6 leading-relaxed">
                Quanto mais usa, melhor fica. Ela aprende preços, serviços, e até seu tom de voz.
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-violet-400" />
                  Importa seu site automaticamente
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-violet-400" />
                  Lê PDFs, catálogos e documentos
                </li>
                <li className="flex items-center gap-3 text-gray-300 text-sm font-inter">
                  <Check className="w-4 h-4 text-violet-400" />
                  Melhora com cada conversa
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="depoimentos" className="py-20 md:py-28 px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-jakarta text-white mb-4">
              Quem usa, recomenda
            </h2>
            <p className="text-gray-400 font-inter">
              Veja o que nossos clientes estão dizendo
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <TestimonialCard
              quote="Antes eu perdia vendas por não responder rápido. Agora a IA responde na hora e eu só fecho o negócio."
              author="Maria Santos"
              role="Loja de Roupas, São Paulo"
              rating={5}
              avatar="/testimonials/maria.png"
            />
            <TestimonialCard
              quote="Meus clientes ficam impressionados com a velocidade. Alguns nem acreditam que é uma IA!"
              author="João Oliveira"
              role="Restaurante Delivery, Rio de Janeiro"
              rating={5}
              avatar="/testimonials/joao.png"
            />
            <TestimonialCard
              quote="Finalmente consigo dormir tranquilo sabendo que nenhum cliente vai ficar sem resposta."
              author="Ana Costa"
              role="Consultora de Beleza, Belo Horizonte"
              rating={5}
              avatar="/testimonials/ana.png"
            />
          </div>
        </div>
      </section>

      {/* Pricing - 3 Plans Only */}
      <section id="precos" className="py-20 md:py-28 px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold font-jakarta text-white mb-4">
              Planos simples, sem surpresas
            </h2>
            <p className="text-gray-400 font-inter text-lg">
              Menos que um salário mínimo. Mais que um funcionário.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <PricingCard
              name="Starter"
              price="R$ 97"
              planId="starter"
              description="Para começar a automatizar"
              features={[
                '1 WhatsApp conectado',
                '500 respostas IA/mês',
                'Modo autônomo 24h',
                '3 documentos de contexto',
                'Suporte via WhatsApp',
              ]}
            />
            <PricingCard
              name="Pro"
              price="R$ 197"
              planId="pro"
              highlighted
              description="Para quem quer crescer"
              features={[
                '1 WhatsApp conectado',
                '2.000 respostas IA/mês',
                'Modo autônomo 24h',
                '10 documentos de contexto',
                'Regras personalizadas',
                'Suporte prioritário',
              ]}
            />
            <PricingCard
              name="Business"
              price="R$ 497"
              planId="enterprise"
              description="Para alta demanda"
              features={[
                '1 WhatsApp conectado',
                '10.000 respostas IA/mês',
                'Modo autônomo 24h',
                'Documentos ilimitados',
                'Áudio com voz clonada',
                'Suporte dedicado',
              ]}
            />
          </div>

          <p className="text-center text-gray-500 text-sm mt-8 font-inter">
            Todos os planos incluem 7 dias grátis. Cancele quando quiser.
          </p>
        </div>
      </section>

      {/* FAQ - Minimal */}
      <section className="py-20 px-4 relative z-10 bg-white/[0.02] border-t border-white/5">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-bold font-jakarta text-white mb-10 text-center">
            Perguntas Frequentes
          </h2>

          <div className="space-y-4">
            {[
              {
                q: 'Preciso saber programar?',
                a: 'Não! É só conectar seu WhatsApp e pronto. A IA aprende sozinha.',
              },
              {
                q: 'Funciona com meu tipo de negócio?',
                a: 'Sim! Lojas, restaurantes, clínicas, serviços... qualquer negócio que atende por WhatsApp.',
              },
              {
                q: 'A IA vai responder errado?',
                a: 'No início você aprova cada resposta. Quando confiar, ativa o automático.',
              },
              {
                q: 'Posso cancelar quando quiser?',
                a: 'Sim! Sem multa, sem burocracia. Cancela em 2 cliques.',
              },
            ].map((faq, i) => (
              <div key={i} className="rounded-xl p-5 bg-white/5 border border-white/10">
                <h3 className="text-white font-semibold font-jakarta mb-2">{faq.q}</h3>
                <p className="text-gray-400 font-inter text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 px-4 relative z-10">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-jakarta text-white mb-6">
            Pronto para vender mais?
          </h2>
          <p className="text-gray-400 font-inter mb-8">
            Comece agora e veja sua IA atendendo em minutos.
          </p>

          <Link
            href="/signup"
            onClick={() => trackEvent({ action: 'signup_start', category: 'conversion', label: 'footer_cta' })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-900 rounded-xl font-semibold font-jakarta text-lg hover:bg-gray-100 transition-all"
          >
            Começar Teste Grátis
            <ArrowRight className="w-5 h-5" />
          </Link>

          <p className="mt-6 text-gray-500 text-sm font-inter">
            7 dias grátis • Sem cartão • Cancele quando quiser
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-4 border-t border-white/5 relative z-10">
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-violet-500 rounded-md flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-gray-400 font-inter text-sm">Atende Turbo</span>
          </div>

          <div className="flex items-center gap-6 text-gray-500 text-sm font-inter">
            <Link href="/termos" className="hover:text-white transition-colors">
              Termos
            </Link>
            <Link href="/privacidade" className="hover:text-white transition-colors">
              Privacidade
            </Link>
            <a href="mailto:suporte@atendeturbo.com" className="hover:text-white transition-colors">
              Contato
            </a>
          </div>

          <div className="text-gray-600 text-sm font-inter">
            © 2025 Atende Turbo
          </div>
        </div>
      </footer>

      <SupportChatWidget />
      <ExitIntentPopup />
    </main>
  );
}
